import type { FilterValues, ReportFilter } from "../api/types";

// URL <-> filter-state codec for the report page. Filters live in the URL
// (?from=...&to=...&f.<filterId>=...) so a refresh or a shared link restores
// the exact same view. React state stays the render source; the URL is the
// persistence layer.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const FILTER_PREFIX = "f.";

export interface ReportParams {
  dateFrom: string;
  dateTo: string;
  filters: FilterValues;
}

function formatDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// Last 30 days in the user's local timezone — the default every report opens
// with unless the URL pins something else.
export function defaultDateRange(now = new Date()): {
  dateFrom: string;
  dateTo: string;
} {
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return { dateFrom: formatDate(from), dateTo: formatDate(now) };
}

// The same calendar day one month earlier, clamped to the last valid day of
// shorter months. DataLens-style "one month ago" therefore maps 31 March to
// 28/29 February instead of overflowing into March.
export function calendarMonthDateRange(now = new Date(), monthsAgo = 1): {
  dateFrom: string;
  dateTo: string;
} {
  const targetMonth = now.getMonth() - monthsAgo;
  const lastTargetDay = new Date(now.getFullYear(), targetMonth + 1, 0).getDate();
  const from = new Date(now);
  from.setFullYear(
    now.getFullYear(),
    targetMonth,
    Math.min(now.getDate(), lastTargetDay),
  );
  return { dateFrom: formatDate(from), dateTo: formatDate(now) };
}

// parseReportParams reads whatever the URL pins. Malformed dates are dropped
// (hand-edited URLs) so callers can fall back to defaults.
export function parseReportParams(search: URLSearchParams): ReportParams {
  const rawFrom = search.get("from") ?? "";
  const rawTo = search.get("to") ?? "";
  const filters: FilterValues = {};
  for (const key of new Set(search.keys())) {
    if (!key.startsWith(FILTER_PREFIX)) continue;
    const id = key.slice(FILTER_PREFIX.length);
    if (!id) continue;
    const values = search.getAll(key).filter((v) => v !== "");
    if (values.length > 0) filters[id] = values;
  }
  return {
    dateFrom: DATE_RE.test(rawFrom) ? rawFrom : "",
    dateTo: DATE_RE.test(rawTo) ? rawTo : "",
    filters,
  };
}

// applyFilterDefaults fills every daterange filter the URL didn't pin with the
// default range, so "last 30 days" is what the user actually sees and runs.
// Multiselect filters stay untouched (empty selection = "all").
export function applyFilterDefaults(
  fromUrl: FilterValues,
  specs: ReportFilter[],
  defaults: { dateFrom: string; dateTo: string },
): FilterValues {
  const filters: FilterValues = { ...fromUrl };
  for (const spec of specs) {
    if (spec.type !== "daterange") continue;
    const pinned = filters[spec.id];
    if (pinned && pinned.length === 2) continue;
    filters[spec.id] = [defaults.dateFrom, defaults.dateTo];
  }
  return filters;
}

// serializeReportParams builds the search string for the current selection.
// Empty values are omitted so untouched state keeps the URL clean.
export function serializeReportParams(params: ReportParams): URLSearchParams {
  const search = new URLSearchParams();
  if (params.dateFrom) search.set("from", params.dateFrom);
  if (params.dateTo) search.set("to", params.dateTo);
  for (const [id, values] of Object.entries(params.filters)) {
    for (const v of values) {
      if (v !== "") search.append(FILTER_PREFIX + id, v);
    }
  }
  return search;
}
